import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { fetchAddressByCEP as fetchAddressByCEPUtil } from '../utils/cep';

// Ícones SVG
const Icon = ({ children, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }}>
    {children}
  </svg>
);


const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${props => props.theme.colors.lightBlue};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 8px;
  
  .required {
    color: ${props => props.theme.colors.error};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.$error ? props.theme.colors.error : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.grayMedium};
  }
  
  &:disabled {
    background-color: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.$error ? props.theme.colors.error : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  margin-top: 5px;
  display: ${props => props.show ? 'block' : 'none'};
`;

const NumberFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  
  input[type="checkbox"] {
    transform: scale(1.2);
    margin: 0;
    cursor: pointer;
  }
  
  label {
    font-size: 14px;
    color: ${props => props.theme.colors.grayDark};
    font-weight: 500;
    cursor: pointer;
    margin: 0;
  }
`;

const ESTADOS = [
  { value: '', label: 'Selecione o estado' },
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

const AddressForm = ({ onDataChange, data, error }) => {
  const [formData, setFormData] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: 'RJ',
    ...data
  });

  const [semNumero, setSemNumero] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    // Construir endereço completo
    const address = `${formData.street || ''}, ${formData.number || ''}${formData.complement ? `, ${formData.complement}` : ''}, ${formData.neighborhood || ''}, ${formData.city || ''} - ${formData.state || ''}, CEP: ${formData.cep || ''}`.trim();
    
    const completeFormData = {
      ...formData,
      address: address
    };
    
    onDataChange(completeFormData);
  }, [formData, onDataChange]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const handleSemNumeroChange = (checked) => {
    setSemNumero(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        number: 'S/N'
      }));
      // Clear error when checking "sem número"
      setFieldErrors(prev => ({
        ...prev,
        number: false
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        number: ''
      }));
    }
  };

  const applyMask = (field, value) => {
    switch (field) {
      case 'cep':
        value = value.replace(/\D/g, '');
        value = value.replace(/(\d{5})(\d)/, '$1-$2');
        return value;
      
      default:
        return value;
    }
  };

  const handleMaskedInputChange = (field, value) => {
    const maskedValue = applyMask(field, value);
    handleInputChange(field, maskedValue);
  };

  const fetchAddressByCEP = async (cep) => {
    const address = await fetchAddressByCEPUtil(cep);
    if (address) {
      setFormData(prev => ({ ...prev, ...address }));
    }
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'cep':
        const cepRegex = /^\d{5}-\d{3}$/;
        return cepRegex.test(value);
      
      default:
        return value.trim() !== '';
    }
  };

  const handleBlur = (field) => {
    const isValid = validateField(field, formData[field]);
    setFieldErrors(prev => ({
      ...prev,
      [field]: !isValid
    }));
    
    // Auto-complete address for CEP
    if (field === 'cep' && isValid) {
      fetchAddressByCEP(formData[field]);
    }
  };

  return (
    <>
      <SectionTitle>Endereço do Titular</SectionTitle>

      <FormGroup>
        <Label htmlFor="cep">
          CEP <span className="required">*</span>
        </Label>
        <Input
          type="text"
          id="cep"
          value={formData.cep}
          onChange={(e) => handleMaskedInputChange('cep', e.target.value)}
          onBlur={() => handleBlur('cep')}
          placeholder="00000-000"
          $error={fieldErrors.cep}
          maxLength="9"
          required
        />
        <ErrorMessage show={fieldErrors.cep}>
          Por favor, informe um CEP válido
        </ErrorMessage>
      </FormGroup>

      <FormRow>
        <FormGroup>
          <Label htmlFor="street">
            Logradouro <span className="required">*</span>
          </Label>
          <Input
            type="text"
            id="street"
            value={formData.street}
            onChange={(e) => handleInputChange('street', e.target.value)}
            onBlur={() => handleBlur('street')}
            placeholder="Rua, Avenida, etc."
            $error={fieldErrors.street}
            required
          />
          <ErrorMessage show={fieldErrors.street}>
            Por favor, informe o logradouro
          </ErrorMessage>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="number">
            Número <span className="required">*</span>
          </Label>
          <NumberFieldContainer>
            <Input
              type="text"
              id="number"
              value={formData.number}
              onChange={(e) => handleInputChange('number', e.target.value)}
              onBlur={() => handleBlur('number')}
              placeholder="123"
              $error={fieldErrors.number}
              disabled={semNumero}
              required
            />
            <CheckboxContainer>
              <input
                type="checkbox"
                id="semNumero"
                checked={semNumero}
                onChange={(e) => handleSemNumeroChange(e.target.checked)}
              />
              <label htmlFor="semNumero">
                Sem número
              </label>
            </CheckboxContainer>
          </NumberFieldContainer>
          <ErrorMessage show={fieldErrors.number}>
            Por favor, informe o número ou marque "Sem número"
          </ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="complement">Complemento</Label>
          <Input
            type="text"
            id="complement"
            value={formData.complement}
            onChange={(e) => handleInputChange('complement', e.target.value)}
            placeholder="Apartamento, bloco, etc."
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="neighborhood">
            Bairro <span className="required">*</span>
          </Label>
          <Input
            type="text"
            id="neighborhood"
            value={formData.neighborhood}
            onChange={(e) => handleInputChange('neighborhood', e.target.value)}
            onBlur={() => handleBlur('neighborhood')}
            placeholder="Nome do bairro"
            $error={fieldErrors.neighborhood}
            required
          />
          <ErrorMessage show={fieldErrors.neighborhood}>
            Por favor, informe o bairro
          </ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="city">
            Cidade <span className="required">*</span>
          </Label>
          <Input
            type="text"
            id="city"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            onBlur={() => handleBlur('city')}
            placeholder="Nome da cidade"
            $error={fieldErrors.city}
            required
          />
          <ErrorMessage show={fieldErrors.city}>
            Por favor, informe a cidade
          </ErrorMessage>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="state">
            Estado <span className="required">*</span>
          </Label>
          <Select
            id="state"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            $error={fieldErrors.state}
            required
          >
            {ESTADOS.map(estado => (
              <option key={estado.value} value={estado.value}>
                {estado.label}
              </option>
            ))}
          </Select>
          <ErrorMessage show={fieldErrors.state}>
            Por favor, selecione o estado
          </ErrorMessage>
        </FormGroup>
      </FormRow>
    </>
  );
};

export default AddressForm;


